if (!customElements.get('bf-poster-preview')) {
  customElements.define(
    'bf-poster-preview',
    class BFPosterPreview extends HTMLElement {
      connectedCallback() {
        this.fileInput = this.querySelector('[data-bf-preview-file]');
        this.uploadPanel = this.querySelector('[data-bf-preview-upload-panel]');
        this.uploadHelp = this.querySelector('[data-bf-preview-upload-help]');
        this.setupTip = this.querySelector('[data-bf-preview-setup-tip]');
        this.skipBoxSetupButton = this.querySelector('[data-bf-preview-skip-setup]');
        this.ensureBoxActionControls();
        this.adjustOpeningButton = this.querySelector('[data-bf-preview-adjust-opening]');
        this.doneOpeningButton = this.querySelector('[data-bf-preview-done-opening]');
        this.posterImages = Array.from(this.querySelectorAll('[data-bf-preview-poster]'));
        this.emptyStates = Array.from(this.querySelectorAll('[data-bf-preview-empty]'));
        this.frames = Array.from(this.querySelectorAll('[data-bf-preview-frame]'));
        this.openings = Array.from(this.querySelectorAll('[data-bf-preview-opening]'));
        this.frameButtons = Array.from(this.querySelectorAll('[data-bf-preview-frame-button]'));
        this.fitButtons = Array.from(this.querySelectorAll('[data-bf-preview-fit]'));
        this.posterControlInputs = Array.from(this.querySelectorAll('[data-bf-preview-control]'));
        this.posterControlOutputs = Array.from(this.querySelectorAll('[data-bf-preview-control-value]'));
        this.openingControlInputs = Array.from(this.querySelectorAll('[data-bf-preview-opening-control]'));
        this.openingControlOutputs = Array.from(this.querySelectorAll('[data-bf-preview-opening-value]'));
        this.resetPosterButton = this.querySelector('[data-bf-preview-reset]');
        this.resetOpeningButton = this.querySelector('[data-bf-preview-reset-opening]');
        this.changePosterButton = this.querySelector('[data-bf-preview-change-poster]');
        this.changeFrameButton = this.querySelector('[data-bf-preview-change-frame]');
        this.activeLabel = this.querySelector('[data-bf-preview-active-label]');
        this.status = this.querySelector('[data-bf-preview-status]');
        this.frameList = this.querySelector('[data-bf-preview-frame-list]');
        this.posterDataUrl = '';
        this.fitMode = 'fit';
        this.dragState = null;
        this.activeFrameKey = '';
        this.linkedGalleryFrameKey = '';
        this.boxSetupComplete = false;
        this.boxEditing = this.classList.contains('is-box-editing');
        this.defaultUploadHelpText = this.uploadHelp?.textContent?.trim() || '';
        this.posterAdjustments = this.defaultPosterAdjustments();
        this.openingStates = new Map();
        this.fallbackFrameImages = [
          'https://cdn.shopify.com/s/files/1/0693/6769/3597/files/E3B0B6CB-58DE-453C-B7CE-6ECD5A3C53E4.jpg?v=1705456810',
          'https://cdn.shopify.com/s/files/1/0693/6769/3597/files/A2FC3C05-3922-405C-946D-35DE1019A689.jpg?v=1705457824',
          'https://cdn.shopify.com/s/files/1/0693/6769/3597/files/1B1A0B85-AA12-4565-872B-93EDA6B3C239.jpg?v=1705454169',
        ];

        this.boundHandleDragMove = (event) => this.handleDragMove(event);
        this.boundHandleDragEnd = (event) => this.handleDragEnd(event);

        this.fileInput?.addEventListener('change', (event) => this.handlePosterUpload(event));
        this.resetPosterButton?.addEventListener('click', () => this.resetPosterAdjustments(true));
        this.resetOpeningButton?.addEventListener('click', () => this.resetOpeningAdjustments(true));
        this.changePosterButton?.addEventListener('click', () => this.fileInput?.click());
        this.changeFrameButton?.addEventListener('click', () => this.focusFrameChooser());
        this.adjustOpeningButton?.addEventListener('click', () => this.setBoxEditing(true, true));
        this.doneOpeningButton?.addEventListener('click', () => {
          this.markBoxSetupComplete('done');
          this.setBoxEditing(false, true);
        });
        this.skipBoxSetupButton?.addEventListener('click', () => {
          this.markBoxSetupComplete('skipped');
          this.setBoxEditing(false, false);
        });

        this.fitButtons.forEach((button) => {
          button.addEventListener('click', () => this.setFitMode(button.dataset.bfPreviewFit || 'fit', true));
        });

        this.posterControlInputs.forEach((input) => {
          input.addEventListener('input', () => this.handlePosterControl(input));
        });

        this.openingControlInputs.forEach((input) => {
          input.addEventListener('input', () => this.handleOpeningControl(input));
        });

        this.linkedGalleryFrameKey = this.addLinkedGalleryFrame();
        this.refreshFrameElements();

        this.openings.forEach((opening) => {
          opening.addEventListener('pointerdown', (event) => this.handleDragStart(event, opening));
          opening.addEventListener('mousedown', (event) => this.handleDragStart(event, opening));
          opening.addEventListener('touchstart', (event) => this.handleDragStart(event, opening));
        });

        window.addEventListener('pointermove', this.boundHandleDragMove);
        window.addEventListener('pointerup', this.boundHandleDragEnd);
        window.addEventListener('pointercancel', this.boundHandleDragEnd);
        window.addEventListener('mousemove', this.boundHandleDragMove);
        window.addEventListener('mouseup', this.boundHandleDragEnd);
        window.addEventListener('touchmove', this.boundHandleDragMove, { passive: false });
        window.addEventListener('touchend', this.boundHandleDragEnd);
        window.addEventListener('touchcancel', this.boundHandleDragEnd);

        this.frameButtons.forEach((button) => {
          button.addEventListener('click', () => this.selectFrame(button.dataset.bfPreviewFrameButton));
        });

        this.applyFallbackFrameImages();
        this.initializeOpeningStates();
        this.applyPosterAdjustments();
        this.setBoxEditing(true, false);
        this.setUploadEnabled(false);
        this.setPosterControlsEnabled(false);

        const initialFrame = this.linkedGalleryFrameKey || this.getInitialFrameKey();
        this.selectFrame(initialFrame || this.frames[0]?.dataset.bfPreviewFrame);
        this.setFitMode('fit', false);
      }

      ensureBoxActionControls() {
        const existingBoxActions = this.querySelector('.bf-poster-preview__box-actions');
        if (existingBoxActions) {
          this.placeBoxActionControls(existingBoxActions);
          return;
        }

        const controls = this.querySelector('.bf-poster-preview__stage-card') || this.querySelector('.bf-poster-preview__controls');

        if (!controls) return;

        const boxActions = document.createElement('div');
        boxActions.className = 'bf-poster-preview__box-actions';
        boxActions.innerHTML = `
          <button class="bf-button" type="button" data-bf-preview-adjust-opening>Adjust Frame Opening</button>
          <button class="bf-button bf-button--ghost" type="button" data-bf-preview-done-opening>Done Adjusting</button>
          <button class="bf-button bf-button--ghost" type="button" data-bf-preview-reset-opening>Reset Box</button>
        `;

        controls.appendChild(boxActions);
        this.placeBoxActionControls(boxActions);
      }

      placeBoxActionControls(boxActions) {
        const stageCard = this.querySelector('.bf-poster-preview__stage-card');
        const frameLabel = this.querySelector('.bf-poster-preview__frame-label');

        if (stageCard && frameLabel) {
          frameLabel.insertAdjacentElement('afterend', boxActions);
        }

        this.querySelectorAll('.bf-poster-preview__box-actions').forEach((node) => {
          if (node !== boxActions) node.remove();
        });
      }

      disconnectedCallback() {
        window.removeEventListener('pointermove', this.boundHandleDragMove);
        window.removeEventListener('pointerup', this.boundHandleDragEnd);
        window.removeEventListener('pointercancel', this.boundHandleDragEnd);
        window.removeEventListener('mousemove', this.boundHandleDragMove);
        window.removeEventListener('mouseup', this.boundHandleDragEnd);
        window.removeEventListener('touchmove', this.boundHandleDragMove);
        window.removeEventListener('touchend', this.boundHandleDragEnd);
        window.removeEventListener('touchcancel', this.boundHandleDragEnd);
      }

      defaultPosterAdjustments() {
        return {
          zoom: 100,
          scaleX: 100,
          scaleY: 100,
          x: 0,
          y: 0,
        };
      }

      smartInitialPosterAdjustments() {
        const opening = this.currentOpeningState();
        const openingSize = opening ? Math.min(opening.width, opening.height) : 64;
        let zoom = 108;

        if (openingSize < 52) zoom = 114;
        if (openingSize < 40) zoom = 122;

        return {
          ...this.defaultPosterAdjustments(),
          zoom,
        };
      }

      defaultOpeningFor(opening) {
        return this.normalizeOpeningState({
          x: this.numberFromDataset(opening, 'bfOpeningDefaultX', 18),
          y: this.numberFromDataset(opening, 'bfOpeningDefaultY', 18),
          width: this.numberFromDataset(opening, 'bfOpeningDefaultWidth', 64),
          height: this.numberFromDataset(opening, 'bfOpeningDefaultHeight', 64),
        });
      }

      initializeOpeningStates() {
        this.openings.forEach((opening) => {
          const frame = opening.closest('[data-bf-preview-frame]');
          const frameKey = frame?.dataset.bfPreviewFrame;
          if (!frameKey) return;

          const state = this.defaultOpeningFor(opening);
          this.openingStates.set(frameKey, state);
          this.applyOpeningState(opening, state);
        });
      }

      refreshFrameElements() {
        this.posterImages = Array.from(this.querySelectorAll('[data-bf-preview-poster]'));
        this.emptyStates = Array.from(this.querySelectorAll('[data-bf-preview-empty]'));
        this.frames = Array.from(this.querySelectorAll('[data-bf-preview-frame]'));
        this.openings = Array.from(this.querySelectorAll('[data-bf-preview-opening]'));
        this.frameButtons = Array.from(this.querySelectorAll('[data-bf-preview-frame-button]'));
      }

      addLinkedGalleryFrame() {
        const params = new URLSearchParams(window.location.search);
        const imageUrl = this.validLinkedImageUrl(params.get('frame_image'));
        if (!imageUrl || !this.frames.length) return '';

        const requestedFrameKey = params.get('frame') || '';
        const baseFrame = this.frames.find((frame) => frame.dataset.bfPreviewFrame === requestedFrameKey) || this.frames[0];
        if (!baseFrame) return '';

        const baseButton =
          this.frameButtons.find((button) => button.dataset.bfPreviewFrameButton === baseFrame.dataset.bfPreviewFrame) ||
          this.frameButtons[0];
        const frameName = this.safeParamText(params.get('frame_name'), 'Gallery frame preview');
        const frameDetail = this.safeParamText(params.get('frame_detail'), 'Gallery preview');
        const frameKey = this.uniqueFrameKey(params.get('gallery_key') || `gallery-${this.slugify(frameName) || 'preview'}`);

        const linkedFrame = baseFrame.cloneNode(true);
        linkedFrame.dataset.bfPreviewFrame = frameKey;
        linkedFrame.dataset.bfPreviewFrameName = frameName;
        linkedFrame.setAttribute('aria-hidden', 'true');
        this.setContainerImage(linkedFrame, imageUrl, frameName, 1200, 1500);
        this.applyLinkedOpeningBounds(linkedFrame, params);
        baseFrame.parentElement?.prepend(linkedFrame);

        if (baseButton) {
          const linkedButton = baseButton.cloneNode(true);
          linkedButton.dataset.bfPreviewFrameButton = frameKey;
          linkedButton.setAttribute('aria-pressed', 'false');
          this.setContainerImage(linkedButton.querySelector('.bf-poster-preview__thumb') || linkedButton, imageUrl, frameName, 300, 375);
          const strong = linkedButton.querySelector('.bf-poster-preview__choice-text strong');
          const detail = linkedButton.querySelector('.bf-poster-preview__choice-text span');
          if (strong) strong.textContent = frameName;
          if (detail) detail.textContent = frameDetail;
          this.frameList?.querySelector('.bf-poster-preview__frame-list')?.prepend(linkedButton);
        }

        return frameKey;
      }

      applyLinkedOpeningBounds(frame, params) {
        const opening = frame?.querySelector('[data-bf-preview-opening]');
        if (!opening) return;

        const state = this.linkedOpeningStateFromParams(params);
        if (!state) return;

        opening.dataset.bfOpeningDefaultX = String(state.x);
        opening.dataset.bfOpeningDefaultY = String(state.y);
        opening.dataset.bfOpeningDefaultWidth = String(state.width);
        opening.dataset.bfOpeningDefaultHeight = String(state.height);
      }

      linkedOpeningStateFromParams(params) {
        const x = this.numberFromParams(params, ['openingX', 'opening_x', 'opening-x']);
        const y = this.numberFromParams(params, ['openingY', 'opening_y', 'opening-y']);
        const width = this.numberFromParams(params, ['openingWidth', 'opening_width', 'opening-width']);
        const height = this.numberFromParams(params, ['openingHeight', 'opening_height', 'opening-height']);

        if ([x, y, width, height].some((value) => value == null)) return null;
        return this.normalizeOpeningState({ x, y, width, height });
      }

      validLinkedImageUrl(value) {
        if (!value) return '';

        try {
          const url = new URL(value, window.location.origin);
          const allowedHost = url.hostname === 'cdn.shopify.com' || url.hostname === window.location.hostname;
          if (!allowedHost || !['http:', 'https:'].includes(url.protocol)) return '';
          return url.href;
        } catch (error) {
          return '';
        }
      }

      safeParamText(value, fallback) {
        const text = String(value || fallback || '').replace(/\s+/g, ' ').trim();
        return text.slice(0, 96) || fallback;
      }

      slugify(value) {
        return String(value || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      uniqueFrameKey(value) {
        const base = this.slugify(value) || 'gallery-preview';
        const existing = new Set(this.frames.map((frame) => frame.dataset.bfPreviewFrame));
        let key = base;
        let index = 2;

        while (existing.has(key)) {
          key = `${base}-${index}`;
          index += 1;
        }

        return key;
      }

      setContainerImage(container, imageUrl, alt, width, height) {
        if (!container) return;

        const existingImage = Array.from(container.children).find((child) => {
          return child.tagName === 'IMG' || child.classList?.contains('placeholder-svg');
        });
        const image = existingImage?.tagName === 'IMG' ? existingImage : document.createElement('img');
        image.src = imageUrl;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.alt = alt;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = width;
        image.height = height;

        if (existingImage && existingImage !== image) {
          existingImage.replaceWith(image);
        } else if (!existingImage) {
          container.prepend(image);
        }
      }

      applyFallbackFrameImages() {
        this.frames.forEach((frame, index) => {
          const hasImage = Array.from(frame.children).some((child) => child.tagName === 'IMG');
          if (!hasImage) {
            const placeholder = Array.from(frame.children).find((child) => child.classList?.contains('placeholder-svg'));
            const image = document.createElement('img');
            image.src = this.fallbackFrameImages[index % this.fallbackFrameImages.length];
            image.alt = 'Temporary Blazin Frames frame mockup image';
            image.loading = 'lazy';
            image.decoding = 'async';
            image.width = 1200;
            image.height = 1500;
            if (placeholder) {
              placeholder.replaceWith(image);
            } else {
              frame.prepend(image);
            }
          }
        });

        this.frameButtons.forEach((button, index) => {
          const thumb = button.querySelector('.bf-poster-preview__thumb');
          if (thumb && !thumb.querySelector('img')) {
            const placeholder = thumb.querySelector('.placeholder-svg');
            const image = document.createElement('img');
            image.src = this.fallbackFrameImages[index % this.fallbackFrameImages.length];
            image.alt = 'Temporary Blazin Frames frame mockup thumbnail';
            image.loading = 'lazy';
            image.decoding = 'async';
            image.width = 300;
            image.height = 375;
            if (placeholder) {
              placeholder.replaceWith(image);
            } else {
              thumb.appendChild(image);
            }
          }
        });
      }

      getInitialFrameKey() {
        const params = new URLSearchParams(window.location.search);
        return params.get('frame');
      }

      handlePosterUpload(event) {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
          this.setStatus('Please choose an image file.');
          return;
        }

        const reader = new FileReader();
        reader.addEventListener('load', () => {
          this.posterDataUrl = String(reader.result || '');
          this.posterImages.forEach((image) => {
            image.src = this.posterDataUrl;
            image.setAttribute('aria-hidden', 'false');
          });
          this.emptyStates.forEach((empty) => {
            empty.hidden = true;
          });
          this.openings.forEach((opening) => {
            opening.classList.add('has-poster');
          });
          this.setPosterControlsEnabled(true);
          this.applySmartInitialPosterFit();
          this.setBoxEditing(false, false);
          this.setStatus(`Previewing ${file.name}. The poster has been centered and sized to fill the opening. Use Fit, Fill, drag, or the sliders for small adjustments. This image is only used locally in your browser.`);
        });
        reader.readAsDataURL(file);
      }

      handlePosterControl(input) {
        const key = input.dataset.bfPreviewControl;
        if (!Object.prototype.hasOwnProperty.call(this.posterAdjustments, key)) return;

        this.posterAdjustments[key] = this.clamp(Number(input.value), Number(input.min), Number(input.max));
        this.applyPosterAdjustments();
      }

      handleOpeningControl(input) {
        const key = input.dataset.bfPreviewOpeningControl;
        const opening = this.activeOpening();
        if (!opening || !this.activeFrameKey) return;

        const current = this.currentOpeningState();
        if (!Object.prototype.hasOwnProperty.call(current, key)) return;

        current[key] = this.clamp(Number(input.value), Number(input.min), Number(input.max));
        const next = this.normalizeOpeningState(current);
        this.openingStates.set(this.activeFrameKey, next);
        this.applyOpeningState(opening, next);
        this.syncOpeningControls();
        this.setBoxEditing(true, false);
        this.markBoxSetupComplete('adjusted');
      }

      handleDragStart(event, opening) {
        if (this.dragState) return;
        if (event.type === 'mousedown' && event.button !== 0) return;

        const point = this.eventPoint(event);
        if (!point) return;

        const frame = opening.closest('[data-bf-preview-frame]');
        if (!frame || frame.getAttribute('aria-hidden') === 'true') return;

        const frameRect = frame.getBoundingClientRect();
        const openingRect = opening.getBoundingClientRect();
        if (!frameRect.width || !frameRect.height || !openingRect.width || !openingRect.height) return;

        const resizeHandle = event.target.closest?.('[data-bf-preview-opening-handle]');
        const moveHandle = event.target.closest?.('[data-bf-preview-opening-move]');

        if (resizeHandle || moveHandle) {
          event.preventDefault();
          this.setBoxEditing(true, false);
          this.startOpeningDrag(event, opening, point, frameRect, resizeHandle?.dataset.bfPreviewOpeningHandle || 'move');
          return;
        }

        if (!this.posterDataUrl) return;

        event.preventDefault();
        this.capturePointer(opening, event);
        opening.classList.add('is-dragging');
        this.dragState = {
          type: 'poster',
          opening,
          pointerId: event.pointerId ?? null,
          startClientX: point.clientX,
          startClientY: point.clientY,
          startX: this.posterAdjustments.x,
          startY: this.posterAdjustments.y,
          width: openingRect.width,
          height: openingRect.height,
        };
      }

      startOpeningDrag(event, opening, point, frameRect, mode) {
        this.capturePointer(opening, event);
        opening.classList.add(mode === 'move' ? 'is-opening-dragging' : 'is-opening-resizing');
        this.dragState = {
          type: mode === 'move' ? 'opening-move' : 'opening-resize',
          mode,
          opening,
          pointerId: event.pointerId ?? null,
          startClientX: point.clientX,
          startClientY: point.clientY,
          startOpening: { ...this.currentOpeningState() },
          frameWidth: frameRect.width,
          frameHeight: frameRect.height,
        };
      }

      handleDragMove(event) {
        if (!this.dragState) return;

        event.preventDefault();
        const point = this.eventPoint(event);
        if (!point) return;

        if (this.dragState.type === 'poster') {
          this.movePoster(point);
          return;
        }

        this.moveOpening(point);
      }

      movePoster(point) {
        const nextX = this.dragState.startX + ((point.clientX - this.dragState.startClientX) / this.dragState.width) * 100;
        const nextY = this.dragState.startY + ((point.clientY - this.dragState.startClientY) / this.dragState.height) * 100;
        this.posterAdjustments.x = this.clamp(Math.round(nextX), -100, 100);
        this.posterAdjustments.y = this.clamp(Math.round(nextY), -100, 100);
        this.applyPosterAdjustments();
      }

      moveOpening(point) {
        const deltaX = ((point.clientX - this.dragState.startClientX) / this.dragState.frameWidth) * 100;
        const deltaY = ((point.clientY - this.dragState.startClientY) / this.dragState.frameHeight) * 100;
        const state = { ...this.dragState.startOpening };

        if (this.dragState.type === 'opening-move') {
          state.x += deltaX;
          state.y += deltaY;
        } else {
          const mode = this.dragState.mode;
          if (mode.includes('e')) state.width += deltaX;
          if (mode.includes('s')) state.height += deltaY;
          if (mode.includes('w')) {
            state.x += deltaX;
            state.width -= deltaX;
          }
          if (mode.includes('n')) {
            state.y += deltaY;
            state.height -= deltaY;
          }
        }

        const next = this.normalizeOpeningState(state);
        this.openingStates.set(this.activeFrameKey, next);
        this.applyOpeningState(this.dragState.opening, next);
        this.syncOpeningControls();
      }

      handleDragEnd(event) {
        if (!this.dragState) return;
        if (event.pointerId && this.dragState.pointerId != null && event.pointerId !== this.dragState.pointerId) return;

        const completedOpeningSetup = ['opening-move', 'opening-resize'].includes(this.dragState.type);
        this.dragState.opening.classList.remove('is-dragging', 'is-opening-dragging', 'is-opening-resizing');
        this.releasePointer(this.dragState.opening, this.dragState.pointerId);
        this.dragState = null;
        if (completedOpeningSetup) this.markBoxSetupComplete('adjusted');
      }

      eventPoint(event) {
        if (event.touches?.length) {
          return event.touches[0];
        }
        if (event.changedTouches?.length) {
          return event.changedTouches[0];
        }
        if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
          return event;
        }
        return null;
      }

      selectFrame(frameKey) {
        if (!frameKey) return;

        let selectedFrame;
        this.frames.forEach((frame) => {
          const selected = frame.dataset.bfPreviewFrame === frameKey;
          frame.setAttribute('aria-hidden', selected ? 'false' : 'true');
          if (selected) selectedFrame = frame;
        });

        if (!selectedFrame) return;

        this.activeFrameKey = frameKey;
        this.frameButtons.forEach((button) => {
          button.setAttribute('aria-pressed', button.dataset.bfPreviewFrameButton === frameKey ? 'true' : 'false');
        });

        const frameName = selectedFrame.dataset.bfPreviewFrameName || 'Selected frame';
        if (this.activeLabel) this.activeLabel.textContent = frameName;
        this.syncOpeningControls();
        this.updateUrlFrame(frameKey);
      }

      updateUrlFrame(frameKey) {
        if (this.linkedGalleryFrameKey && frameKey === this.linkedGalleryFrameKey) return;

        const url = new URL(window.location.href);
        url.searchParams.set('frame', frameKey);
        if (this.linkedGalleryFrameKey && frameKey !== this.linkedGalleryFrameKey) {
          url.searchParams.delete('frame_image');
          url.searchParams.delete('frame_name');
          url.searchParams.delete('frame_detail');
          url.searchParams.delete('gallery_key');
        }
        window.history.replaceState({}, '', url);
      }

      setFitMode(mode, resetView = false) {
        this.applyFitMode(mode);

        if (resetView) {
          this.resetPosterAdjustments(false);
        } else {
          this.applyPosterAdjustments();
        }
      }

      applySmartInitialPosterFit() {
        this.applyFitMode('fill');
        this.posterAdjustments = this.smartInitialPosterAdjustments();
        this.applyPosterAdjustments();
      }

      applyFitMode(mode) {
        this.fitMode = mode === 'fill' ? 'fill' : 'fit';
        this.posterImages.forEach((image) => {
          image.dataset.fit = this.fitMode;
        });
        this.fitButtons.forEach((button) => {
          button.setAttribute('aria-pressed', button.dataset.bfPreviewFit === this.fitMode ? 'true' : 'false');
        });
      }

      resetPosterAdjustments(updateStatus = true) {
        this.posterAdjustments = this.defaultPosterAdjustments();
        this.applyPosterAdjustments();
        if (updateStatus) {
          this.setStatus(
            this.posterDataUrl
              ? 'Poster view reset. Drag the poster or use the sliders to fine tune it.'
              : 'Upload a poster image to start previewing.'
          );
        }
      }

      resetOpeningAdjustments(updateStatus = true) {
        const opening = this.activeOpening();
        if (!opening || !this.activeFrameKey) return;

        const next = this.defaultOpeningFor(opening);
        this.openingStates.set(this.activeFrameKey, next);
        this.applyOpeningState(opening, next);
        this.syncOpeningControls();
        this.setBoxEditing(true, false);
        if (updateStatus) {
          this.setStatus('Opening reset. Move or resize the crop box to match the frame photo.');
        }
      }

      applyPosterAdjustments() {
        const zoom = this.posterAdjustments.zoom / 100;
        const scaleX = this.posterAdjustments.scaleX / 100;
        const scaleY = this.posterAdjustments.scaleY / 100;

        this.posterImages.forEach((image) => {
          image.style.setProperty('--bf-poster-x', `${this.posterAdjustments.x}%`);
          image.style.setProperty('--bf-poster-y', `${this.posterAdjustments.y}%`);
          image.style.setProperty('--bf-poster-zoom', zoom);
          image.style.setProperty('--bf-poster-scale-x', scaleX);
          image.style.setProperty('--bf-poster-scale-y', scaleY);
        });

        this.syncPosterControls();
      }

      applyOpeningState(opening, state) {
        opening.style.setProperty('--bf-opening-x', `${this.roundForControl(state.x)}%`);
        opening.style.setProperty('--bf-opening-y', `${this.roundForControl(state.y)}%`);
        opening.style.setProperty('--bf-opening-width', `${this.roundForControl(state.width)}%`);
        opening.style.setProperty('--bf-opening-height', `${this.roundForControl(state.height)}%`);
      }

      syncPosterControls() {
        this.posterControlInputs.forEach((input) => {
          const key = input.dataset.bfPreviewControl;
          if (Object.prototype.hasOwnProperty.call(this.posterAdjustments, key)) {
            input.value = this.posterAdjustments[key];
          }
        });

        this.posterControlOutputs.forEach((output) => {
          const key = output.dataset.bfPreviewControlValue;
          if (!Object.prototype.hasOwnProperty.call(this.posterAdjustments, key)) return;

          output.textContent = ['zoom', 'scaleX', 'scaleY'].includes(key)
            ? `${this.posterAdjustments[key]}%`
            : String(this.posterAdjustments[key]);
        });
      }

      syncOpeningControls() {
        const state = this.currentOpeningState();
        if (!state) return;

        this.openingControlInputs.forEach((input) => {
          const key = input.dataset.bfPreviewOpeningControl;
          if (Object.prototype.hasOwnProperty.call(state, key)) {
            input.value = this.roundForControl(state[key]);
          }
        });

        this.openingControlOutputs.forEach((output) => {
          const key = output.dataset.bfPreviewOpeningValue;
          if (Object.prototype.hasOwnProperty.call(state, key)) {
            output.textContent = `${this.roundForControl(state[key])}%`;
          }
        });
      }

      setPosterControlsEnabled(enabled) {
        this.posterControlInputs.forEach((input) => {
          input.disabled = !enabled;
        });
      }

      setBoxEditing(editing, updateStatus = false) {
        this.boxEditing = !!editing;
        this.classList.toggle('is-box-editing', this.boxEditing);

        if (!updateStatus) return;

        this.setStatus(
          this.boxEditing
            ? 'Frame opening controls are visible. Move or resize the box, then tap Done Adjusting.'
            : 'Frame opening controls hidden. Tap Adjust Frame Opening if you need to refine the box.'
        );
      }

      setUploadEnabled(enabled) {
        if (this.fileInput) this.fileInput.disabled = !enabled;
        this.uploadPanel?.classList.toggle('is-disabled', !enabled);
        this.uploadPanel?.setAttribute('aria-disabled', enabled ? 'false' : 'true');

        if (!this.uploadHelp) return;
        this.uploadHelp.textContent = enabled
          ? this.defaultUploadHelpText
          : 'First match the preview box to the frame opening, or choose Skip box setup.';
      }

      markBoxSetupComplete(reason = 'adjusted') {
        if (this.boxSetupComplete) return;

        this.boxSetupComplete = true;
        this.setUploadEnabled(true);
        if (this.setupTip) this.setupTip.hidden = true;
        this.setStatus(
          reason === 'skipped'
            ? 'Upload unlocked. You can still move or resize the opening after your poster is added.'
            : 'Opening set. Upload your poster, then use Fit, Fill, or small adjustments.'
        );
      }

      focusFrameChooser() {
        this.frameList?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const pressedButton = this.frameButtons.find((button) => button.getAttribute('aria-pressed') === 'true');
        pressedButton?.focus({ preventScroll: true });
      }

      activeOpening() {
        const frame = this.frames.find((item) => item.dataset.bfPreviewFrame === this.activeFrameKey);
        return frame?.querySelector('[data-bf-preview-opening]') || null;
      }

      currentOpeningState() {
        if (!this.activeFrameKey) return null;
        const existing = this.openingStates.get(this.activeFrameKey);
        if (existing) return { ...existing };

        const opening = this.activeOpening();
        return opening ? this.defaultOpeningFor(opening) : null;
      }

      normalizeOpeningState(state) {
        const next = {
          x: this.clamp(Number(state.x), 0, 90),
          y: this.clamp(Number(state.y), 0, 90),
          width: this.clamp(Number(state.width), 10, 100),
          height: this.clamp(Number(state.height), 10, 100),
        };

        next.width = Math.min(next.width, 100 - next.x);
        next.height = Math.min(next.height, 100 - next.y);

        if (next.width < 10) {
          next.width = 10;
          next.x = Math.min(next.x, 90);
        }
        if (next.height < 10) {
          next.height = 10;
          next.y = Math.min(next.y, 90);
        }

        return next;
      }

      numberFromDataset(element, key, fallback) {
        const value = Number(element.dataset[key]);
        return Number.isFinite(value) ? value : fallback;
      }

      numberFromParams(params, names) {
        for (const name of names) {
          const rawValue = params.get(name);
          if (rawValue == null || rawValue === '') continue;
          const value = Number(rawValue);
          if (Number.isFinite(value)) return value;
        }

        return null;
      }

      setStatus(message) {
        if (this.status) this.status.textContent = message;
      }

      capturePointer(element, event) {
        if (event.pointerId != null) {
          element.setPointerCapture?.(event.pointerId);
        }
      }

      releasePointer(element, pointerId) {
        if (pointerId != null) {
          element.releasePointerCapture?.(pointerId);
        }
      }

      clamp(value, min, max) {
        if (Number.isNaN(value)) return min;
        return Math.min(Math.max(value, min), max);
      }

      roundForControl(value) {
        return Math.round(value);
      }
    }
  );
}
